import { create } from 'zustand';
import { AuthUser, User, Account, ImpersonationInfo } from '../types';
import { apiService } from './api';

interface AuthState {
  user: User | null;
  account: Account | null;
  impersonation: ImpersonationInfo | null;
  /** Whether this session is on Stripe test keys. Owned by the server session. */
  testModeEnabled: boolean;
  loading: boolean;
  setTestModeEnabled: (enabled: boolean) => void;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
  setAuth: (authUser: AuthUser | null) => void;
  updateUser: (user: User) => void;
  updateAccount: (account: Account) => void;
  refreshAuth: () => Promise<void>;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  // Legacy setter for backward compatibility
  setUser: (user: User | null) => void;
}

const TRANSIENT_AUTH_ERRORS = ['NETWORK_ERROR', 'SERVER_UNAVAILABLE'];
const AUTH_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000];

export const useAuthStore =create<AuthState>((set, get) => ({
  user: null,
  account: null,
  impersonation: null,
  testModeEnabled: false,
  loading: true,

  setTestModeEnabled: (enabled) => set({ testModeEnabled: enabled }),

  checkAuth: async () => {
    try {
      let response = await apiService.getMe();

      // A failure that isn't a 401/403 is the server being unreachable, not the
      // session being gone. Treating it as signed out sent people to the sign-in
      // page whenever the first load of the day caught the server waking up, even
      // though their cookie was still good. Keep trying for about a minute.
      for (const delay of AUTH_RETRY_DELAYS_MS) {
        if (response.success || !TRANSIENT_AUTH_ERRORS.includes(response.error.code)) break;
        await new Promise(resolve => setTimeout(resolve, delay));
        response = await apiService.getMe();
      }

      if (response.success) {
        set({
          user: response.data.user,
          account: response.data.account,
          impersonation: response.data.impersonation || null,
          testModeEnabled: response.data.testModeEnabled === true,
          loading: false
        });
      } else {
        set({ user: null, account: null, impersonation: null, testModeEnabled: false, loading: false });
      }
    } catch (error) {
      set({ user: null, account: null, impersonation: null, testModeEnabled: false, loading: false });
    }
  },

  logout: async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Logout failed - silently continue
    } finally {
      set({ user: null, account: null, impersonation: null, testModeEnabled: false });
    }
  },

  setAuth: (authUser) => {
    if (authUser) {
      set({
        user: authUser.user,
        account: authUser.account,
        impersonation: authUser.impersonation || null,
        testModeEnabled: authUser.testModeEnabled === true
      });
    } else {
      set({ user: null, account: null, impersonation: null, testModeEnabled: false });
    }
  },

  updateUser: (user) => set({ user }),

  updateAccount: (account) => set({ account }),

  refreshAuth: async () => {
    try {
      // Use getAccount instead of getMe to get full account data including organization contact info
      const response = await apiService.getAccount();
      
      if (response.success) {
        // Only update the account, keep existing user and impersonation data
        set((state) => ({ 
          ...state,
          account: response.data
        }));
      } else {
        throw new Error(response.error?.message || 'Failed to refresh account data')
      }
    } catch (error) {
      throw error; // Re-throw to allow calling code to handle the error
    }
  },

  startImpersonation: async (userId: string) => {
    try {
      const response = await apiService.startImpersonation(userId);

      if (response.success) {
        // Refresh auth state to get impersonated user data
        await get().checkAuth();
      } else {
        throw new Error(response.error.message);
      }
    } catch (error) {
      throw error;
    }
  },

  stopImpersonation: async () => {
    try {
      const response = await apiService.stopImpersonation();

      if (response.success) {
        // Refresh auth state to get admin user data
        await get().checkAuth();
      } else {
        throw new Error(response.error.message);
      }
    } catch (error) {
      throw error;
    }
  },

  // Legacy setter for backward compatibility
  setUser: (user) => set({ user }),
}));
